import AccountCircle from "@mui/icons-material/AccountCircle";
import { Divider, Grid, Menu, MenuItem } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { hasPermission, PolicyAction, PolicyResource } from "@vmosolutions/oc-interfaces";
import { Auth } from "aws-amplify";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { formatDateString } from "src/components/DateDisplay";
import { IconButton } from "src/components/IconButton/IconButton";
import { MenuLinks } from "src/components/Navigation/MenuLinks";
import { ShortCodeSearch } from "src/components/ShortCodes/ShortCodeSearch";
import { ToggleUIDS } from "src/redux/actions/App.Actions";
import { appendNodeToFirstValidLeaf, openWindow, saveMosaicLayout } from "src/redux/actions/Mosaic.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import LogoIcon from "src/themes/assets/logo_icon.svg?react";
import { IMosaicTabNode } from "src/utils/MosaicTree";
import { isDevEnv } from "src/utils/index";
import { v1 } from "uuid";
import { AppLaunchPad } from "./AppLaunchpad";
import { ColoredBackground } from "./ColoredBars/ColoredBars";

interface IVersionInfo {
  branch: string;
  built: number;
  hash: string;
  tags: string;
}

const RenderAppBar = (): JSX.Element => {
  const utility = useSelector((state: IRootState) => state.utility);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const dispatch = useDispatch();
  const versionInfoString = localStorage.getItem("oc_version") ?? null;
  const versionInfo: IVersionInfo = versionInfoString ? JSON.parse(versionInfoString) : null;
  const authentication = useSelector((state: IRootState) => state.authentication);
  const { displayUID } = utility;
  const currentUser = !!authentication && !!authentication.attributes ? authentication.attributes.firstName : null;
  const userIsVMO = authentication?.attributes?.email?.includes("@vmo.aero");
  const windowManager = useSelector((state: IRootState) => state.windowManager);

  const isLaunchPadOpen = useCallback(() => {
    return (
      //@ts-ignore
      (windowManager.currentNode.hasOwnProperty("tabNodes") && windowManager.currentNode.tabNodes.length > 0) ||
      windowManager.currentNode.hasOwnProperty("direction")
    );
  }, [windowManager]);

  //Main Nav Memu
  function handleNavMenuClick(event: any): void {
    if (menuAnchor !== event.currentTarget) {
      setMenuAnchor(event.currentTarget);
    }
  }
  function handleNavMenuClose(): void {
    setMenuAnchor(null);
  }

  //Use Menu
  const handleMenu = (event: any): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleUserMenuClose = (path?: string): void => {
    setAnchorEl(null);
  };

  const saveLayout = useCallback(() => {
    dispatch(saveMosaicLayout());
  }, [dispatch]);

  const openWiki = (): void => {
    AmplifyAPIWrapper.Get<{ url: string }>("/user/wikilink").then((x) => {
      if (x?.url) window.open(x.url, "_blank");
    });
  };

  const openPreferences = (): void => {
    handleClickLink({
      componentName: "UserPreferencesWindow",
      id: v1(),
      title: "User Preferences",
      props: {},
      type: "content",
    });
  };

  const handleLogout = async (): Promise<void> => {
    await Auth.signOut();
    window.location.replace("/");
  };

  const handleClickLink = (node: IMosaicTabNode): void => {
    {
      const { title, componentName, props, type } = node;
      if (type === "singleContext" || type === "context") {
        //@ts-ignore
        dispatch(openWindow(title, componentName, [], props, type, true));
      } else {
        dispatch(appendNodeToFirstValidLeaf(node));
      }
    }
  };

  const toggleUIDs = (): void => {
    const { displayUID } = utility;
    dispatch(ToggleUIDS(!displayUID));
  };

  // Return admin status
  const isAdmin = (): boolean => {
    if (!!authentication && !!authentication.attributes && !!authentication.attributes.permissions) {
      return !!authentication.attributes.permissions.admin;
    }
    return false;
  };
  return (
    <>
      <AppBar
        sx={(theme) => ({
          backgroundColor: theme.custom.appBar.backgroundColor,
          zIndex: theme.zIndex.drawer + 1,
          height: 36,
          px: 0,
          position: "relative",
        })}
      >
        <ColoredBackground showName={true} />
        <Toolbar
          sx={{
            minHeight: "30px !important",
          }}
          disableGutters={true}
        >
          <Grid
            container
            spacing={0}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: 0,
            }}
          >
            <Grid item container xs spacing={0} sx={{ display: "flex", alignItems: "center" }}>
              <Grid item sx={{ p: 0, m: 0, display: "flex", alignItems: "center", paddingLeft: "10px" }}>
                {isLaunchPadOpen() === true && <MenuLinks />}
                <LogoIcon
                  style={{
                    width: "40px",
                    marginLeft: "10px",
                  }}
                />
              </Grid>
              <Grid item>
                <ShortCodeSearch />
              </Grid>
            </Grid>
            <Grid item>
              {!!currentUser && (
                <div>
                  <Typography
                    sx={(theme) => ({
                      display: "inline",
                      userSelect: "none",
                      color: theme.palette.primary.contrastText,
                    })}
                    variant="body1"
                    color="inherit"
                    noWrap={true}
                  >
                    {currentUser}
                  </Typography>

                  <IconButton
                    className="account_menu_btn"
                    id="account_menu_btn"
                    aria-owns={open ? "menu-appbar" : null}
                    aria-haspopup="true"
                    onClick={handleMenu}
                    color="inherit"
                    size="large"
                    sx={{ px: "6px", margin: 0 }}
                  >
                    <AccountCircle />
                  </IconButton>

                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    open={Boolean(anchorEl)}
                    onClose={handleUserMenuClose}
                  >
                    <MenuItem onClick={openWiki}>Documentation</MenuItem>
                    <MenuItem onClick={saveLayout}>Save Current Layout</MenuItem>
                    <MenuItem onClick={openPreferences}>Preferences</MenuItem>

                    {authentication.attributes.allowInternalSignIn && (
                      <MenuItem
                        onClick={() =>
                          handleClickLink({
                            componentName: "ChangePasswordWindow",
                            id: "changePassword",
                            title: "Change Password",
                            props: {},
                            type: "singleContext",
                          })
                        }
                      >
                        Change Password
                      </MenuItem>
                    )}
                    {hasPermission(authentication?.attributes?.policies, PolicyResource.Api, PolicyAction.Read) && (
                      <MenuItem
                        onClick={() =>
                          handleClickLink({
                            componentName: "APIKeyWindow",
                            id: "APIKey",
                            title: "API Key",
                            props: {},
                            type: "singleContext",
                          })
                        }
                      >
                        API Key
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                    {(isDevEnv() || isAdmin) && <Divider />}
                    {(isDevEnv() || isAdmin) && (
                      <MenuItem
                        disableRipple
                        sx={{
                          p: 0,
                          my: 0,
                          "&:hover": {
                            backgroundColor: "transparent",
                            cursor: "default",
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={(theme) => ({
                            fontSize: "0.7em",
                            fontWeight: 900,
                            textTransform: "uppercase",
                            textAlign: "center",
                            padding: "4px 18px",
                            color: theme.palette.primary.dark,
                          })}
                        >
                          Administration
                        </Typography>
                      </MenuItem>
                    )}
                    {isDevEnv() && isAdmin && (
                      <MenuItem onClick={toggleUIDs}>{displayUID ? "Hide UIDs" : "Display UIDs"}</MenuItem>
                    )}
                    {isAdmin && versionInfo && (
                      <Grid container direction="column" style={{ padding: "10px 18px" }}>
                        <Typography style={{ fontSize: 12 }}>
                          Branch: {versionInfo?.branch ?? "Unknown"} (Tags: {versionInfo?.tags ?? "None"})
                          {versionInfo?.built
                            ? `, built on ${formatDateString(versionInfo?.built, "Etc/UTC", true, false)}`
                            : ""}
                        </Typography>
                        <Typography style={{ fontSize: 12 }}>SHA: {versionInfo?.hash ?? "Unknown"}</Typography>
                      </Grid>
                    )}
                    {isAdmin && userIsVMO && (
                      <MenuItem
                        onClick={() =>
                          handleClickLink({
                            componentName: "APIVersionInfoWindow",
                            id: "APIVersion",
                            title: "API Version Information",
                            props: {},
                            type: "singleContext",
                          })
                        }
                      >
                        API Version Info
                      </MenuItem>
                    )}
                  </Menu>
                </div>
              )}
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <AppLaunchPad handleClickLink={handleClickLink} />
    </>
  );
};

export default RenderAppBar;
